<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const shouldBeApiBasedUrl = (value = '') =>
  value ? value.startsWith('http') : true;

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      channelName: '',
      apiBaseUrl: '',
      keyId: '',
      secret: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  validations: {
    channelName: { required },
    apiBaseUrl: { shouldBeApiBasedUrl },
    keyId: { required },
    secret: { required },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const salesforceChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.channelName,
            channel: {
              type: 'salesforce',
              api_base_url: this.apiBaseUrl,
              key_id: this.keyId,
              secret: this.secret,
            },
          }
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: salesforceChannel.id,
          },
        });
      } catch (error) {
        useAlert(
          this.$t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div
    class="border border-n-weak bg-n-solid-1 rounded-t-lg border-b-0 h-full w-full p-6 col-span-6 overflow-auto"
  >
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.DESC')"
    />
    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model="channelName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
            "
            @blur="v$.channelName.$touch"
          />
          <span v-if="v$.channelName.$error" class="message">{{
            $t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.CHANNEL_NAME.ERROR')
          }}</span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.apiBaseUrl.$error }">
          {{ $t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.API_BASE_URL.LABEL') }}
          <input
            v-model="apiBaseUrl"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.API_BASE_URL.PLACEHOLDER')
            "
            @blur="v$.apiBaseUrl.$touch"
          />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.API_BASE_URL.SUBTITLE') }}
        </p>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.keyId.$error }">
          {{ $t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.KEY_ID.LABEL') }}
          <input
            v-model="keyId"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.KEY_ID.PLACEHOLDER')
            "
            @blur="v$.keyId.$touch"
          />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.KEY_ID.SUBTITLE') }}
        </p>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.secret.$error }">
          {{ $t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.SECRET.LABEL') }}
          <input
            v-model="secret"
            type="password"
            :placeholder="
              $t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.SECRET.PLACEHOLDER')
            "
            @blur="v$.secret.$touch"
          />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.SECRET.SUBTITLE') }}
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
